import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useDialog } from "../../hooks/UseDialog";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Typography,
    useTheme,
} from "@mui/material";
import Fetch from "../../services/Fetch";
import { useNavigate, useParams } from "react-router-dom";
import AlertDialog from "../../components/DialogView";
import SnackbarAlert from "../../components/SnackBar";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";

function questionTypeLabel(type, isAr) {
    if (type === "multiple_options") return isAr ? "اختيار من متعدد" : "Multiple choice";
    if (type === "true_false") return isAr ? "صح أو خطأ" : "True or false";
    return type;
}

function Options() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { open, title, description, setDialog, setOpen } = useDialog();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [question, setQuestion] = useState(null);
    const [examName, setExamName] = useState("");
    const [optionId, setOptionId] = useState("");
    const param = useParams();
    const navigate = useNavigate();

    const questionsPath = `/courses/${param.course_id}/exams/${param.exam_id}/questions`;
    const createOptionPath = `/courses/${param.course_id}/exams/${param.exam_id}/questions/${param.question_id}/create-option`;

    const options = question?.options || [];
    const isTrueFalse = question?.type === "true_false";
    const correctCount = useMemo(
        () => options.filter((o) => o.is_correct).length,
        [options]
    );

    const questionText = useMemo(() => {
        if (!question) return "";
        return isAr ? question.text_ar : question.text_en;
    }, [question, isAr]);

    const trueFalseCorrectValue = useMemo(() => {
        const correct = options.find((option) => option.is_correct);
        if (!correct) return "true";
        return correct.text_en === "True" || correct.text_ar === "صح" ? "true" : "false";
    }, [options]);

    const setCorrectAnswer = async (value) => {
        setSendWait(true);
        const formData = new FormData();
        formData.append("correct_answer", value === "true" ? "1" : "0");

        const result = await Fetch(
            `${host}/teacher/courses/exams/questions/${param.question_id}/update`,
            "POST",
            formData
        );

        if (result.status === 200) {
            setSnackBar("success", isAr ? "تم تحديث الإجابة الصحيحة" : "Correct answer updated");
            await loadQuestion();
        } else if (result.status === 422) {
            setSnackBar("error", result.data.errors?.[0] || result.data.message);
        } else {
            setSnackBar("error", isAr ? "فشل تحديث الإجابة" : "Failed to update answer");
        }

        setSendWait(false);
    };

    const loadQuestion = async () => {
        setGetWait(true);
        const result = await Fetch(
            `${host}/courses/${param.course_id}/exams/${param.exam_id}/show`,
            "GET",
            null
        );

        if (result.status === 200) {
            const exam = result.data.data;
            setExamName(isAr ? exam.name_ar : exam.name_en);
            const found = (exam.questions || []).find(
                (q) => String(q.id) === String(param.question_id)
            );
            setQuestion(found || null);
        }

        setGetWait(false);
    };

    const deleteOption = async () => {
        setSendWait(true);
        const result = await Fetch(
            `${host}/teacher/courses/exams/questions/options/${optionId}/delete`,
            "DELETE",
            null
        );

        if (result.status === 200) {
            setQuestion((prev) => ({
                ...prev,
                options: (prev?.options || []).filter((item) => item.id !== optionId),
            }));
            setSnackBar("success", isAr ? "تم حذف الخيار" : "Option deleted");
            setOpen(false);
            setOptionId("");
        }

        setSendWait(false);
    };

    useEffect(() => {
        loadQuestion();
    }, [param.course_id, param.exam_id, param.question_id]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isTrueFalse ? (isAr ? "إجابة صح أو خطأ" : "True/false answer") : (isAr ? "خيارات الإجابة" : "Answer options")}
                    subtitle={
                        isTrueFalse
                            ? (isAr ? "خياران ثابتان: صح وخطأ — حدد الإجابة الصحيحة فقط" : "Fixed options: True and False — select the correct answer only")
                            : (isAr ? "إدارة خيارات السؤال وتحديد الإجابة الصحيحة" : "Manage question options and mark the correct answer")
                    }
                    icon={<ListAltOutlinedIcon />}
                    actions={
                        <>
                            {!isTrueFalse ? (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate(createOptionPath)}
                                    sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                                >
                                    {isAr ? "إضافة خيار" : "Add option"}
                                </Button>
                            ) : null}
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(questionsPath)}
                                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                            >
                                {isAr ? "عودة للأسئلة" : "Back to questions"}
                            </Button>
                        </>
                    }
                />

                {question ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isAr ? "الاختبار" : "Exam"}: {examName}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C", mt: 0.5 }}>
                                    {questionText}
                                </Typography>
                            </Box>
                            <Chip
                                label={questionTypeLabel(question.type, isAr)}
                                sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }}
                            />
                            <Chip label={`${isAr ? "الدرجة" : "Mark"}: ${question.full_mark}`} />
                            <Chip label={`${isAr ? "الخيارات" : "Options"}: ${options.length}`} />
                            <Chip
                                label={`${isAr ? "إجابات صحيحة" : "Correct"}: ${correctCount}`}
                                sx={{ bgcolor: correctCount > 0 ? "#f0fdf4" : "#fef2f2", color: correctCount > 0 ? "#166534" : "#991b1b" }}
                            />
                        </Box>
                    </ScrollReveal>
                ) : !getWait ? (
                    <Typography sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                        {isAr ? "تعذر تحميل السؤال" : "Could not load question"}
                    </Typography>
                ) : null}

                <DashboardPanelCard
                    title={isTrueFalse ? (isAr ? "الإجابة الصحيحة" : "Correct answer") : (isAr ? "قائمة الخيارات" : "Options list")}
                    subtitle={
                        isTrueFalse
                            ? (isAr ? "لا حاجة لإضافة خيارات يدوياً" : "No manual options needed")
                            : (options.length
                                ? (isAr ? `${options.length} خيار لهذا السؤال` : `${options.length} option(s) for this question`)
                                : (isAr ? "لا توجد خيارات بعد" : "No options yet"))
                    }
                >
                    {isTrueFalse ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <FormControl component="fieldset" disabled={sendWait}>
                                <RadioGroup
                                    row
                                    value={trueFalseCorrectValue}
                                    onChange={(e) => setCorrectAnswer(e.target.value)}
                                >
                                    <FormControlLabel value="true" control={<Radio color="success" />} label={isAr ? "صح" : "True"} />
                                    <FormControlLabel value="false" control={<Radio color="success" />} label={isAr ? "خطأ" : "False"} />
                                </RadioGroup>
                            </FormControl>
                            {sendWait ? <CircularProgress size={24} sx={{ alignSelf: "flex-start" }} /> : null}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {options.map((option) => (
                                    <Box
                                        key={option.id}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            border: "2px solid",
                                            borderColor: option.is_correct ? "#86efac" : (theme.palette.mode === "dark" ? "divider" : "#e2e8f0"),
                                            bgcolor: option.is_correct
                                                ? (theme.palette.mode === "dark" ? "rgba(22,163,74,0.12)" : "#f0fdf4")
                                                : (theme.palette.mode === "dark" ? "background.default" : "#f8fafc"),
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0D358C", flex: 1 }}>
                                            {isAr ? option.text_ar : option.text_en}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            icon={option.is_correct ? <CheckCircleOutlineIcon /> : <CancelOutlinedIcon />}
                                            label={option.is_correct ? (isAr ? "إجابة صحيحة" : "Correct answer") : (isAr ? "إجابة خاطئة" : "Incorrect")}
                                            sx={{
                                                bgcolor: option.is_correct ? "#dcfce7" : "#f1f5f9",
                                                color: option.is_correct ? "#166534" : "#64748b",
                                                fontWeight: 700,
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : options.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <ListAltOutlinedIcon sx={{ fontSize: 56, color: "#94a3b8", mb: 1 }} />
                            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                                {isAr ? "لم تُضف خيارات إجابة لهذا السؤال بعد" : "No answer options added yet"}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(createOptionPath)}
                                sx={{ bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                            >
                                {isAr ? "إضافة أول خيار" : "Add first option"}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {options.map((option, index) => (
                                <Box
                                    key={option.id}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        alignItems: { xs: "stretch", md: "center" },
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        border: "2px solid",
                                        borderColor: option.is_correct
                                            ? "#86efac"
                                            : (theme.palette.mode === "dark" ? "divider" : "#e2e8f0"),
                                        bgcolor: option.is_correct
                                            ? (theme.palette.mode === "dark" ? "rgba(22,163,74,0.12)" : "#f0fdf4")
                                            : (theme.palette.mode === "dark" ? "background.default" : "#f8fafc"),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            bgcolor: option.is_correct ? "#16a34a" : "#64748b",
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 800,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {String.fromCharCode(65 + index)}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0D358C" }}>
                                            {isAr ? option.text_ar : option.text_en}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            icon={option.is_correct ? <CheckCircleOutlineIcon /> : <CancelOutlinedIcon />}
                                            label={option.is_correct ? (isAr ? "إجابة صحيحة" : "Correct answer") : (isAr ? "إجابة خاطئة" : "Incorrect")}
                                            sx={{
                                                mt: 0.5,
                                                bgcolor: option.is_correct ? "#dcfce7" : "#f1f5f9",
                                                color: option.is_correct ? "#166534" : "#64748b",
                                                fontWeight: 700,
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flexShrink: 0 }}>
                                        {!isTrueFalse ? (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditOutlinedIcon />}
                                                    onClick={() => navigate(`/courses/${param.course_id}/exams/${param.exam_id}/questions/${param.question_id}/update-option/${option.id}`)}
                                                >
                                                    {isAr ? "تعديل" : "Edit"}
                                                </Button>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setOptionId(option.id);
                                                        setDialog(
                                                            isAr ? "حذف الخيار" : "Delete option",
                                                            isAr ? "هل أنت متأكد من حذف هذا الخيار؟" : "Are you sure you want to delete this option?"
                                                        );
                                                    }}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </>
                                        ) : null}
                                    </Box>
                                </Box>
                            ))}

                            {!isTrueFalse ? (
                                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => navigate(createOptionPath)}
                                        sx={{ bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                                    >
                                        {isAr ? "إضافة خيار جديد" : "Add new option"}
                                    </Button>
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </DashboardPanelCard>
            </PageContent>

            <AlertDialog
                title={title}
                description={description}
                onCancel={() => setOpen(false)}
                onConfirm={deleteOption}
                wait={sendWait}
                openDialog={open}
            />
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Options;
